import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  'Drive Sports and Snap! Raise are both built around the team drive, not the individual athlete. If you\'re fundraising solo — or your team campaign ended and your season costs didn\'t — here\'s where Sport Fund fits.';

const FAQS = [
  {
    q: 'Is Drive Sports built for individual athletes?',
    a: "No. Drive Sports is built around the team \"drive\" — a coach or program runs one campaign for a whole roster, with team hierarchies, participant leaderboards and athletic-director oversight. There's no athlete-owned page for ongoing, year-round personal support. Sport Fund is the opposite: each athlete creates and owns their own page.",
  },
  {
    q: 'How does Drive Sports pricing compare to Sport Fund?',
    a: "Drive Sports charges the team or program a flat monthly platform fee (publicly cited around $29–99/month) plus card/ACH processing, and is priced per program, not per athlete. Sport Fund has no monthly fee for athletes at all — it's free to create a page, and the athlete keeps 100% of every donation; a transparent fee (~5% bank, ~7% card) is added on top and paid by the supporter at checkout.",
  },
  {
    q: 'What happens to individual costs after a team drive ends?',
    a: "A team drive is a single campaign with a start and end date — once it wraps, the fundraising stops even though individual costs (travel, gear, private coaching, tournament fees) keep coming. Sport Fund is built for ongoing, recurring support so an athlete's page keeps working for the whole season, not just one campaign window.",
  },
  {
    q: 'Can a coach use both a team drive and Sport Fund?',
    a: "Yes — they solve different problems. A team drive (Drive Sports, Snap! Raise) covers shared program costs like uniforms or a bus. Sport Fund covers what's specific to one athlete: their own travel, gear, coaching or competition fees, funded by their own family and friends network.",
  },
  {
    q: 'Does Sport Fund support recurring monthly donations like a team drive can?',
    a: "Yes, and it's recurring by default rather than a bolt-on — supporters can back an athlete with a monthly gift for the season, not just a one-time push tied to a single campaign window.",
  },
];

const DriveSportsAndTeamFundraisers: React.FC = () => {
  usePageMeta(
    'Drive Sports & Team Fundraising Apps: Where They Leave Individual Athletes Out (2026)',
    ARTICLE_DESCRIPTION,
    {
      article: true,
      image: '/resources/coach-and-athlete.jpg',
      breadcrumbs: [
        { name: 'Resources', path: '/resources' },
        { name: 'Drive Sports & Team Fundraisers' },
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    }
  );
  const TOC_SECTIONS = [
    {
      id: "what-team-first-actually-means-in-the-product",
      text: "What \"team-first\" actually means in the product"
    },
    {
      id: "where-that-leaves-the-individual-athlete",
      text: "Where that leaves the individual athlete"
    },
    {
      id: "an-honest-comparison",
      text: "An honest comparison"
    },
    {
      id: "they-re-not-competitors-they-solve-different-problems",
      text: "They're not competitors — they solve different problems"
    },
    {
      id: "who-should-consider-sport-fund",
      text: "Who should consider Sport Fund"
    },
    {
      id: "start-in-minutes",
      text: "Start in minutes"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        Drive Sports, Snap! Raise & team fundraising apps: built for the program, not the athlete
      </h1>
      <p className="text-gray-500 mb-8 text-lg">
        Team fundraising platforms solve a real problem — for the team. Here's what they leave out for the individual athlete, and where Sport Fund fits.
      </p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          Search "fundraising for youth sports" and most of what comes back — Drive Sports, Snap! Raise, Vertical Raise,
          Givebutter's sports templates — is the same shape: a coach or booster club launches one campaign, the whole
          roster sells or shares it, and the program collects the total. That's a genuinely useful tool for shared
          costs like uniforms, a team bus, or tournament entry. It is not built to answer a different, very common
          question: <strong>how does one athlete cover what's specific to them</strong> — travel, private coaching,
          gear, a competition entry fee their program doesn't cover?
        </p>

        <h2 id="what-team-first-actually-means-in-the-product">What "team-first" actually means in the product</h2>
        <p>
          Drive Sports, for example, organizes everything around teams, participants and "drives" (a named campaign
          with a start and end date), plus coach dashboards and athletic-director oversight — features built for
          running one program's fundraiser, not for a single athlete's ongoing support. It charges the program a flat
          monthly platform fee (publicly listed around $29–$99/month depending on plan) plus standard card/ACH
          processing, on top of the campaign itself. Snap! Raise works similarly — one team campaign, run for the
          group, reported to widely take roughly 20% of what's raised. Neither is priced or built around one athlete
          owning a page.
        </p>

        <h2 id="where-that-leaves-the-individual-athlete">Where that leaves the individual athlete</h2>
        <ul>
          <li>
            <strong>The campaign ends, the costs don't.</strong> A "drive" has a start and end date. Travel, gear, and
            coaching bills keep arriving all season.
          </li>
          <li>
            <strong>One pool, split many ways.</strong> Team-drive money is raised for the program and typically
            distributed or spent at the program's discretion — not earmarked to cover one athlete's specific,
            individual expenses.
          </li>
          <li>
            <strong>No page the athlete owns.</strong> There's no lasting, personal link an athlete can keep sharing
            with their own network — grandparents, family friends, a former coach — after the team campaign closes.
          </li>
          <li>
            <strong>No recurring support built in as the default.</strong> Team drives are structured as one push, not
            an ongoing monthly relationship with an athlete's own supporters.
          </li>
        </ul>

        <h2 id="an-honest-comparison">An honest comparison</h2>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Drive Sports</th>
                <th>Snap! Raise</th>
                <th>Sport Fund</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Built around</strong></td>
                <td>Team "drives" run by a coach/program</td>
                <td>Team / booster campaigns</td>
                <td><strong>The individual athlete's own page</strong></td>
              </tr>
              <tr>
                <td><strong>Who pays the platform</strong></td>
                <td>Flat monthly fee to the program (~$29–99/mo) + processing</td>
                <td>~20% widely reported to come out of what's raised</td>
                <td><strong>$0 to the athlete — a transparent supporter-side fee (~5% bank/~7% card) added at checkout</strong></td>
              </tr>
              <tr>
                <td><strong>Campaign length</strong></td>
                <td>Fixed-window "drive"</td>
                <td>Fixed-window campaign</td>
                <td><strong>Ongoing — the page stays live for the season and beyond</strong></td>
              </tr>
              <tr>
                <td><strong>Recurring monthly giving</strong></td>
                <td>Not the core model</td>
                <td>Not supported — one-time only</td>
                <td><strong>Built-in, recurring-first</strong></td>
              </tr>
              <tr>
                <td><strong>Payout</strong></td>
                <td>To the program</td>
                <td>To the program</td>
                <td><strong>Straight to the athlete/family bank</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-gray-500">
          Figures for other platforms reflect publicly reported 2026 pricing/reviews and may vary by agreement or plan;
          confirm current terms with each provider. Sport Fund is not affiliated with, endorsed by, or sponsored by
          Drive Sports or Snap! Raise. Names are used only to compare services (nominative reference).
        </p>

        <h2 id="they-re-not-competitors-they-solve-different-problems">They're not competitors — they solve different problems</h2>
        <p>
          A team drive is the right tool for a shared program cost with a clear end date. Sport Fund is the right tool
          for what's specific to one athlete and doesn't stop when the drive does. Plenty of programs use both: a
          Drive Sports or Snap! Raise campaign for the team bus, and a Sport Fund page for each athlete's own travel,
          coaching, and gear — funded by the people who already know that athlete personally.
        </p>

        <h2 id="who-should-consider-sport-fund">Who should consider Sport Fund</h2>
        <p>
          Individual athletes and families across 30+ sports whose costs don't fit neatly into a team campaign — or
          whose team doesn't run one at all — who want ongoing, recurring support from their own network.
        </p>

        <h2 id="start-in-minutes">Start in minutes</h2>
        <p>
          Setting up a fund page is free and takes a few minutes. Tell your story, set a target, and share it with
          your own network — no team drive required.
        </p>
        <p>
          <Link to="/signup-creator" className="text-blue-600">Start a free fund page</Link> — keep 100% of every donation.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/snap-raise-alternatives" className="text-blue-600">Snap! Raise Alternatives in 2026</Link> — a closer look at Snap!'s reported fees and how Sport Fund compares.
        </p>
        <p>
          <Link to="/resources/spotfund-vs-sport-fund" className="text-blue-600">Sport Fund vs Spotfund NIL: Not the Same Platform</Link> — clearing up the name confusion with a similarly-named NIL platform.
        </p>
        <ArticleFeedback articleSlug="drive-sports-and-team-fundraisers" />
      </article>
    </div>
  );
};

export default DriveSportsAndTeamFundraisers;
